import React from "react";
import { Link } from "react-router-dom";
import { useWizard } from "@/context/WizardContext";
import { Grid, TextInput, SectionHeading, FieldLabel } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { fmtMDY } from "@/lib/dateFormat";
import { FIELD_26_TEXT, FIELD_28_TEXT } from "@/lib/sampleData";

const LIMITATIONS = ["Amputation", "Bowel/Bladder Incontinence", "Contracture", "Hearing", "Paralysis", "Endurance", "Ambulation", "Speech", "Legally Blind", "Dyspnea with Minimal Exertion", "Other"];

function ChipGroup({ label, options, selected = [], onChange, testid }) {
  const toggle = (o) => {
    const next = selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o];
    onChange(next);
  };
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3 h-9 rounded-full text-[12.5px] font-medium border transition ${on ? "bg-[#0A3D79] text-[#F7FAFC] border-[#0A3D79]" : "bg-white text-[#0B1E3A] border-[#D6E3EF] hover:border-[#1FA0E6]"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Read-only label + value row used to echo data pulled from earlier steps.
function Pulled({ label, value, sourceStep, sourceLabel, testid }) {
  const isEmpty = !value || (Array.isArray(value) && value.length === 0);
  const display = Array.isArray(value) ? value.join(", ") : value;
  return (
    <div className="rounded-lg border border-[#D6E3EF] bg-[#F7FAFC] px-3 py-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">{label}</div>
        {sourceStep && (
          <Link
            to={`/wizard/${sourceStep}`}
            className="text-[10.5px] text-[#0A3D79] hover:underline inline-flex items-center gap-1"
          >
            {sourceLabel || `from Step ${sourceStep}`} <ExternalLink className="w-2.5 h-2.5" />
          </Link>
        )}
      </div>
      <div className={`mt-1 text-[13.5px] ${isEmpty ? "text-[#9AADC2] italic" : "text-[#0B1E3A]"}`}>
        {isEmpty ? "— not entered —" : display}
      </div>
    </div>
  );
}

export default function Step7CMS485() {
  const { data, update } = useWizard();
  const cms = data.cms485 || {};
  const client = data.client || {};
  const agency = data.agency || {};
  const physician = data.physician || {};
  const assessment = data.assessment || {};
  const mh = data.medical_history || {};
  const communication = data.communication || {};
  const sig = data.signatures || {};

  // Canonical pulls — prefer the Step 7 (NSA) structured field, with CMS-485
  // back-compat fallback so old drafts still show something.
  const prognosisPulled = assessment.prognosis?.prognosis || cms.prognosis || "";
  const nutritionPulled = assessment.nutrition?.diet || cms.nutritional_req || "";
  const activitiesPulled = assessment.activities?.permitted || cms.activities_permitted || [];
  const allergiesPulled = [
    mh.drug_allergies && `Drugs: ${mh.drug_allergies}`,
    mh.food_allergies && `Food: ${mh.food_allergies}`,
    mh.allergies && `Other: ${mh.allergies}`,
  ].filter(Boolean).join(" · ") || (cms.allergies || "");
  const cognitionPulled = communication.cognition || cms.mental_status || "";

  return (
    <div className="space-y-8">
      {/* ---------- Hero: explanatory banner -------------------------- */}
      <section className="card-surface fade-up bg-gradient-to-b from-[#FFFDEC] to-[#FFF3B3] border-[#DBB90A]">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <CheckCircle2 className="w-5 h-5 text-[#DBB90A]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#8A6B00] mb-1">
              Step 8 · Plan of Care · Review &amp; Finalize
            </div>
            <h2 className="font-display text-2xl font-semibold text-[#072A54]">
              Almost done — just the certification window and 3 free-text blocks.
            </h2>
            <p className="text-[13.5px] text-[#1E2E4A] mt-2 max-w-2xl">
              Everything else on this page is <span className="font-semibold">pulled directly from earlier steps</span>.
              No retyping. If a pulled field is wrong, click the "from Step N" link next to it to fix it at the source.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Editable: cert window + type ---------------------- */}
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 8 · Editable" title="Certification Period"
          description="The only dates that can only be set here — drives ForwardHealth authorization window." />
        <Grid cols={3}>
          <TextInput label="Certification Type" testid="cms-cert-type"
            value={cms.cert_type} onChange={(v) => update("cms485", { cert_type: v })}
            placeholder="Initial / Recertification" />
          <TextInput label="Period From" type="date" testid="cms-from"
            value={cms.cert_period_from} onChange={(v) => update("cms485", { cert_period_from: v })} />
          <TextInput label="Period To" type="date" testid="cms-to"
            value={cms.cert_period_to} onChange={(v) => update("cms485", { cert_period_to: v })} />
          <TextInput label="Nurse Verbal SOC Date" type="date" testid="cms-verbal-soc"
            value={cms.nurse_verbal_soc} onChange={(v) => update("cms485", { nurse_verbal_soc: v })} />
          <TextInput label="HHA Receipt Date" type="date" testid="cms-hha-received"
            value={cms.hha_received_date} onChange={(v) => update("cms485", { hha_received_date: v })} />
          <TextInput label="Physician Signature Date" type="date" testid="cms-phys-sign-date"
            value={cms.physician_sign_date} onChange={(v) => update("cms485", { physician_sign_date: v })} />
        </Grid>
      </section>

      {/* ---------- Editable: orders, DME, goals, safety, rehab ------ */}
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 8 · Editable" title="Physician Orders &amp; Goals"
          description="Free-text blocks that only make sense on the CMS-485. Use voice dictation + narrative polish to speed through these." />
        <div className="space-y-4">
          <NarrativeField label="Orders &amp; Treatments"
            fieldKey="cms485.orders_treatments"
            fieldPurpose="Specific physician orders for PCW tasks, frequency, and duration. Pair with PCW staffing grid."
            testid="cms-orders"
            value={cms.orders_treatments} onChange={(v) => update("cms485", { orders_treatments: v })} />
          <NarrativeField label="DME &amp; Supplies"
            fieldKey="cms485.dme_supplies"
            fieldPurpose="Durable medical equipment the PCW uses or maintains — walker, Hoyer lift, oxygen, CPAP, hospital bed."
            testid="cms-dme"
            value={cms.dme_supplies} onChange={(v) => update("cms485", { dme_supplies: v })} />
          <NarrativeField label="Safety Measures"
            fieldKey="cms485.safety_measures"
            fieldPurpose="Fall precautions, infection controls, emergency plan, home-specific hazards mitigated."
            testid="cms-safety"
            value={cms.safety_measures} onChange={(v) => update("cms485", { safety_measures: v })} />
          <NarrativeField label="Rehab Potential"
            fieldKey="cms485.rehab_potential"
            fieldPurpose="Short statement — maintenance vs. improvement goals; functional trajectory."
            testid="cms-rehab"
            value={cms.rehab_potential} onChange={(v) => update("cms485", { rehab_potential: v })} />
          <NarrativeField label="Goals / Discharge Plan"
            fieldKey="cms485.goals_discharge"
            fieldPurpose="Measurable goals and the discharge/transition plan when cert ends."
            testid="cms-goals"
            value={cms.goals_discharge} onChange={(v) => update("cms485", { goals_discharge: v })} />
        </div>
      </section>

      {/* ---------- Editable: functional limitations checklist ------- */}
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 8 · Editable" title="Functional Limitations (CMS-485 Field 18)"
          description="Complements the ADL scores — CMS uses this simplified limitation checklist." />
        <ChipGroup
          label="Limitations (select all that apply)"
          options={LIMITATIONS}
          selected={cms.functional_limitations}
          onChange={(v) => update("cms485", { functional_limitations: v })}
          testid="cms-limitations"
        />
      </section>

      {/* ---------- Pulled from earlier steps ------------------------- */}
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 8 · Pulled from earlier steps" title="Read-only auto-fills"
          description="These values are pulled straight into the CMS-485 and 1-page POC PDFs. Fix the source, not here." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Pulled testid="pulled-client" label="Client" sourceStep={1} sourceLabel="Step 1 · Client"
            value={`${client.last_name || ""}, ${client.first_name || ""}`.replace(/^,\s*|,\s*$/g, "")} />
          <Pulled testid="pulled-dob" label="DOB" sourceStep={1}
            value={fmtMDY(client.date_of_birth)} />
          <Pulled testid="pulled-memberid" label="Member ID" sourceStep={1}
            value={client.forwardhealth_member_id} />
          <Pulled testid="pulled-agency" label="Agency / Provider #" sourceStep={1}
            value={`${agency.name || "—"} · ${agency.provider_number || "—"}`} />
          <Pulled testid="pulled-physician" label="Physician / NPI" sourceStep={1}
            value={`${physician.name || "—"} · ${physician.npi || "—"}`} />
          <Pulled testid="pulled-allergies" label="Allergies" sourceStep={5} sourceLabel="Step 5 · Allergies"
            value={allergiesPulled || "NKDA"} />
          <Pulled testid="pulled-cognition" label="Mental Status / Cognition" sourceStep={4} sourceLabel="Step 4 · Cognition"
            value={cognitionPulled} />
          <Pulled testid="pulled-prognosis" label="Prognosis" sourceStep={7} sourceLabel="Step 7 · Prognosis"
            value={prognosisPulled} />
          <Pulled testid="pulled-nutrition" label="Nutrition / Diet" sourceStep={7} sourceLabel="Step 7 · Nutrition"
            value={nutritionPulled} />
          <Pulled testid="pulled-activities" label="Activities Permitted" sourceStep={7} sourceLabel="Step 7 · Activities"
            value={activitiesPulled} />
          <Pulled testid="pulled-dx-count" label="Diagnoses (count)" sourceStep={5} sourceLabel="Step 5 · Diagnoses"
            value={`${(data.diagnoses || []).length} diagnoses on file`} />
          <Pulled testid="pulled-meds-count" label="Medications (count)" sourceStep={5} sourceLabel="Step 5 · Medications"
            value={`${(data.medications || []).length} medications on file`} />
        </div>
      </section>

      {/* ---------- Verbatim regulatory blocks ----------------------- */}
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 8 · Verbatim" title="CMS-485 Field 26 &amp; Field 28"
          description="These two blocks print verbatim on the PDF. You never type them — shown here so you know what the physician is signing." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-[#D6E3EF] bg-[#F7FAFC] px-3 py-3">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#3A5275] mb-1">
              Field 26 — Physician Certification
            </div>
            <div className="text-[11.5px] text-[#1E2E4A] leading-relaxed" data-testid="cms-field26">
              {FIELD_26_TEXT}
            </div>
          </div>
          <div className="rounded-lg border border-[#D6E3EF] bg-[#F7FAFC] px-3 py-3">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#3A5275] mb-1">
              Field 28 — Fraud Warning
            </div>
            <div className="text-[11.5px] text-[#1E2E4A] leading-relaxed" data-testid="cms-field28">
              {FIELD_28_TEXT}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Physician signer (pulled + editable) ------------ */}
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 8 · Pulled" title="Signers"
          description="RN and Agency Representative signatures are captured in Step 10 (Triple Verification). Physician signs after fax." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Pulled testid="pulled-rn-signer" label="RN signer" sourceStep={10} sourceLabel="Step 10 · Signatures"
            value={sig.rn_name} />
          <Pulled testid="pulled-rep-signer" label="Agency Representative" sourceStep={10} sourceLabel="Step 10 · Signatures"
            value={sig.agency_rep_name} />
          <Pulled testid="pulled-physician-signer" label="Physician (prints on CMS-485)" sourceStep={1} sourceLabel="Step 1 · Physician"
            value={physician.name} />
        </div>
      </section>
    </div>
  );
}
